import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { postBusinessCases, } from '@zurich/api/document';
import { useApi, useDownloadLink, useSnackbar, useTranslation, } from '@zurich/common/hooks';
import { InsuranceCompanyCodes } from '@zurich/common/types';
import { offerAtom } from '@zurich-client/recoil/contract';
/**
 * usePostBusinessCases is used to create a document for a given
 * insurance confirmation number.
 *
 * @returns
 * downloadVbNumber - async function to create VB document
 * loading - boolean
 * DownloadLink - ReactNode containing attributes for document download
 */
export function usePostBusinessCases(saveOffer) {
    const { t } = useTranslation();
    const { flashError } = useSnackbar();
    const offer = useRecoilValue(offerAtom);
    const { fetch } = useApi({
        request: postBusinessCases,
        options: {
            errorText: t('document.errors.downloadDocument'),
        },
    });
    const { setDownloadData } = useDownloadLink();
    const [loading, setLoading] = useState(false);
    const handleFetch = async (documentData) => await fetch({
        params: { body: documentData },
    });
    const downloadDocumentLocally = async (documentDataPayload) => {
        setLoading(true);
        const businessCaseBaseResponse = await handleFetch(documentDataPayload);
        if (businessCaseBaseResponse) {
            if (businessCaseBaseResponse.documentResponses &&
                businessCaseBaseResponse.documentResponses.length > 0 &&
                businessCaseBaseResponse.documentResponses[0].fileAsBase64 &&
                businessCaseBaseResponse.documentResponses[0].fileName) {
                setLoading(false);
                setDownloadData({
                    data: businessCaseBaseResponse.documentResponses[0].fileAsBase64,
                    filename: businessCaseBaseResponse.documentResponses[0].fileName,
                });
                return businessCaseBaseResponse;
            }
            setLoading(false);
            flashError({ message: t('common:snackbar.error.downloadError') });
            return null;
        }
        setLoading(false);
        flashError({ message: t('common:snackbar.error.downloadError') });
        return null;
    };
    // FIXME: We have two option: Either we make a new function for every businessCaseDocumentDefinitionId we come across
    // or we call it downloadDocument and pass the parameter accordingly
    const documentDataPayload = (vbNumber) => ({
        businessCaseDocumentDefinitionId: 'MotorInsuranceConfirmation_Instant',
        relatedId: `${InsuranceCompanyCodes.ZURICH}*${vbNumber}`,
    });
    const createDocumentPolicyPayload = (policyNumber) => ({
        businessCaseDocumentDefinitionId: 'MotorIIC_Instant',
        relatedId: policyNumber ?? '',
    });
    const documentDataWithOfferIdPayload = {
        businessCaseDocumentDefinitionId: 'MotorWaiverBonusLevel_Instant',
        relatedId: offer?.id ?? '',
    };
    const downloadVbNumberDocument = async (vbNumber) => {
        const response = await downloadDocumentLocally(documentDataPayload(vbNumber));
        return response;
    };
    const downloadDocumentPolicyPayload = async (policyNumber) => {
        const response = await downloadDocumentLocally(createDocumentPolicyPayload(policyNumber));
        return response;
    };
    const downloadBonusLevelDocument = () => {
        if (offer?.id && saveOffer) {
            void saveOffer(offer?.id);
        }
        void downloadDocumentLocally(documentDataWithOfferIdPayload);
    };
    return {
        downloadVbNumberDocument,
        downloadBonusLevelDocument,
        downloadDocumentPolicyPayload,
        loading,
    };
}
